/**
 * Experiment Store — Opt-In A/B Testing Infrastructure
 *
 * Experiments are DISABLED by default. Every visitor gets `defaultVariant`.
 *
 * Activation modes:
 * 1. ?experiment=<id>           → random variant assignment (persisted to localStorage)
 * 2. ?experiment=<id>&variant=v → explicit variant selection (persisted)
 * 3. No params                  → defaultVariant, zero surprises
 *
 * Persistence: once activated, the variant sticks via localStorage across reloads.
 * Tracking: VARIANT_ASSIGNED fires only for opted-in users.
 */

import { defineStore } from 'pinia';
import { ref } from 'vue';
import logger from '../../utils/logger';

// ─── Experiment definitions ────────────────────────────────────────────────

export interface ExperimentDefinition {
	readonly id: string;
	readonly variants: Readonly<Record<string, string>>;
	readonly defaultVariant: string;
}

/**
 * The Break Page A/B experiment.
 *
 * - control (default): Skip the break page → go directly to phase 2.
 * - break: Show the motivational break page between phase 1 and phase 2.
 */
export const BREAK_PAGE_EXPERIMENT = {
	id: 'break_page_v1',
	variants: {
		CONTROL: 'control',
		BREAK: 'break',
	},
	defaultVariant: 'control',
} as const satisfies ExperimentDefinition;

export type BreakPageVariant =
	(typeof BREAK_PAGE_EXPERIMENT.variants)[keyof typeof BREAK_PAGE_EXPERIMENT.variants];

// ─── Store ─────────────────────────────────────────────────────────────────

const STORAGE_KEY_PREFIX = 'experiment_v1_';

export const useExperimentStore = defineStore('experiment', () => {
	/**
	 * Map from experimentId → assigned variantId (string).
	 * Populated lazily on first access.
	 */
	const assignments = ref<Record<string, string>>({});

	/**
	 * Set of experiment IDs that were activated via URL params in this session.
	 * Used to distinguish "opted-in" from "default" for tracking purposes.
	 */
	const activatedExperiments = ref<Set<string>>(new Set());

	// ─── Helpers ─────────────────────────────────────────────────────────────

	/**
	 * Read a persisted variant from localStorage.
	 */
	function readStoredVariant(experimentId: string): string | null {
		try {
			return localStorage.getItem(`${STORAGE_KEY_PREFIX}${experimentId}`);
		} catch {
			return null;
		}
	}

	/**
	 * Persist a variant assignment to localStorage.
	 */
	function storeVariant(experimentId: string, variantId: string): void {
		try {
			localStorage.setItem(`${STORAGE_KEY_PREFIX}${experimentId}`, variantId);
		} catch {
			// localStorage may be unavailable in some environments — log and continue
			logger.warn('Could not persist experiment variant', { experimentId, variantId });
		}
	}

	/**
	 * Deterministically pick a variant based on a seed string.
	 * Uses a simple djb2-style hash to produce a stable bucket [0, 1).
	 * The experiment `defaultVariant` is used as the 0-bucket.
	 */
	function pickVariant<E extends ExperimentDefinition>(experiment: E, seed: string): string {
		const variantValues = Object.values(experiment.variants);
		if (variantValues.length === 0) return experiment.defaultVariant;

		let hash = 5381;
		for (let i = 0; i < seed.length; i++) {
			hash = (hash * 33) ^ seed.charCodeAt(i);
		}
		const bucket = Math.abs(hash) % variantValues.length;
		return variantValues[bucket] ?? experiment.defaultVariant;
	}

	// ─── Public actions ───────────────────────────────────────────────────────

	/**
	 * Get the variant for the given experiment.
	 *
	 * Priority order:
	 * 1. Already assigned in memory (same session)
	 * 2. Persisted localStorage assignment (returning opted-in visitor)
	 * 3. Default variant (experiment not activated)
	 *
	 * NOTE: URL param activation is handled by `activateExperiment()`,
	 * which is called from the router guard — NOT from here.
	 *
	 * @returns The assigned variant string (e.g. 'control' | 'break')
	 */
	function getOrAssignVariant<E extends ExperimentDefinition>(experiment: E): string {
		const { id: experimentId } = experiment;

		// 1. Already in memory for this session
		const inMemory = assignments.value[experimentId];
		if (inMemory !== undefined) {
			return inMemory;
		}

		// 2. Persisted assignment (returning opted-in visitor)
		const persisted = readStoredVariant(experimentId);
		if (persisted !== null && Object.values(experiment.variants).includes(persisted)) {
			assignments.value[experimentId] = persisted;
			return persisted;
		}

		// 3. Not activated → use default variant (no surprise behavior)
		const variant = experiment.defaultVariant;
		assignments.value[experimentId] = variant;
		return variant;
	}

	/**
	 * Activate an experiment via URL params (called from router guard).
	 *
	 * - With explicitVariant: force that specific variant
	 * - Without explicitVariant: random bucket via pickVariant
	 *
	 * Persists to localStorage and marks the experiment as activated for tracking.
	 *
	 * @returns The assigned variant string
	 */
	function activateExperiment<E extends ExperimentDefinition>(
		experiment: E,
		explicitVariant?: string,
	): string {
		const { id: experimentId } = experiment;

		let variant: string;

		if (explicitVariant && Object.values(experiment.variants).includes(explicitVariant)) {
			// Explicit variant selection
			variant = explicitVariant;
			logger.info('Experiment: explicit variant applied', { experimentId, variantId: variant });
		} else {
			// Random bucket assignment
			const seed = `${experimentId}__${Date.now()}__${Math.random().toString(36).slice(2)}`;
			variant = pickVariant(experiment, seed);
			logger.info('Experiment: random variant assigned', { experimentId, variantId: variant });
		}

		assignments.value[experimentId] = variant;
		activatedExperiments.value.add(experimentId);
		storeVariant(experimentId, variant);

		return variant;
	}

	/**
	 * Check if an experiment was activated (opted-in) vs using default.
	 */
	function isExperimentActivated(experimentId: string): boolean {
		return activatedExperiments.value.has(experimentId);
	}

	/**
	 * Check if the break page is enabled for the current user.
	 */
	function isBreakPageEnabled(): boolean {
		const variant = getOrAssignVariant(BREAK_PAGE_EXPERIMENT);
		return variant === BREAK_PAGE_EXPERIMENT.variants.BREAK;
	}

	/**
	 * Force-override a variant in memory (used for testing and dev tooling).
	 * Also persists to localStorage.
	 */
	function overrideVariant(experimentId: string, variantId: string): void {
		assignments.value[experimentId] = variantId;
		activatedExperiments.value.add(experimentId);
		storeVariant(experimentId, variantId);
		logger.info('Experiment: variant overridden', { experimentId, variantId });
	}

	/**
	 * Clear a persisted experiment assignment (resets to default on next visit).
	 */
	function clearExperiment(experimentId: string): void {
		delete assignments.value[experimentId];
		activatedExperiments.value.delete(experimentId);
		try {
			localStorage.removeItem(`${STORAGE_KEY_PREFIX}${experimentId}`);
		} catch {
			// ignore
		}
		logger.info('Experiment: cleared', { experimentId });
	}

	return {
		assignments,
		activatedExperiments,
		getOrAssignVariant,
		activateExperiment,
		isExperimentActivated,
		isBreakPageEnabled,
		overrideVariant,
		clearExperiment,
	};
});
